/**
 * OrbitDetailRail — Variant B companion detail panel for the Power-Mix Orbit.
 *
 * Clicking (or focusing) a node sets the active site; this rail explains it
 * from REAL data already on the OrbitSite — no new scoring, no fabrication:
 *
 *   - Verdict            → DerivedSiteVerdict.bandLabel / bandColor as plain
 *                          text for a PUBLISHED band only. A held or withheld
 *                          site carries no badge (owner, 2026-09-30); the WHY
 *                          block is titled with that state instead.
 *   - Stage checklist    → composite.decisionStages — the TWO-STAGE pipeline
 *                          (Stage 1 early screening / Stage 2 mature validation)
 *                          with each required analysis ticked when present and
 *                          the promoted-scenario prerequisite named for Stage 2.
 *   - WHY                → the hold reason (held) / verdict.adequacyReason
 *                          (withheld) / composite.decisionRationale (published),
 *                          plus the missing pillars (composite.pillars[*].missing).
 *   - Resolve → <tab>     → direct navigation to the site tab that owns the
 *                          next step (Analyses to run / review an analysis,
 *                          Intent to declare the power strategy, the Site Map
 *                          for the study area, else the decision — what still
 *                          holds GO) for withheld / constrained sites.
 *   - Readiness + Coverage → two DISTINCT metric tiles (readiness toward GO —
 *                          or stage progress while no band is published — vs
 *                          evidence coverage) — the E1 two-channel split.
 *   - Power-mix bar      → OrbitSite.mix (BTM / GRID / NUC MW), GAP-honest.
 *   - Decision pillars   → all 10 PILLARS with their composite score (missing
 *                          pillars shown distinctly as "awaiting data").
 *   - Data trust         → composite.confidenceTag (V / R / C) + one-line meaning.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { bandGlowColorVar } from '../shared/verdictVisuals';
import {
  PILLAR_IDS,
  PILLARS,
  isAdvisoryPillar,
  isDecisionApplicablePillar,
} from '../../lib/portfolio/pillarRegistry';
import { decisionPillarCoverage } from '../../lib/portfolio/deriveSiteVerdict';
import { DECISION_STAGE_LABEL, REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { screeningScoreLabel } from '../../lib/pathToGo/stageBoards';
import type { SavedScreeningReportContext } from '../../lib/pathToGo/savedScreeningContext';
import type { DecisionStageStatus, DecisionStagesStatus } from '../../types/portfolioComposite';
import { siteDecisionTab } from '../../lib/siteDecisionTab';
import { isDecisionFindingsV1Enabled } from '../../lib/runtimeFlags';
import {
  nextStepFor,
  unassessedNext,
  type MixCategory,
  type OrbitNextStepTarget,
  type OrbitSite,
  type OrbitStage,
  type UnassessedNext,
} from './twoActDerivation';

const MIX_LABEL: Record<MixCategory, string> = { btm: 'BTM', grid: 'GRID', nuclear: 'NUC' };
const MIX_ORDER: MixCategory[] = ['btm', 'grid', 'nuclear'];

const TRUST_MEANING: Record<'V' | 'R' | 'C', string> = {
  V: 'Verified — cited to source documents.',
  R: 'Reported — provider data, light corroboration.',
  C: 'Coarse — QA or provenance caveats remain.',
};

/**
 * The site tab a next step's Resolve opens, and its name — read per render so
 * a rolled-back build (no Decision tab) routes and labels the decision as
 * Analyses / Reports, where Decide then renders.
 */
function resolveOrbitTarget(target: OrbitNextStepTarget): { tab: string; label: string } {
  const analysesLabel = isDecisionFindingsV1Enabled() ? 'Analyses' : 'Reports';
  switch (target) {
    case 'map':
      return { tab: 'map', label: 'Site Map' };
    case 'intent':
      return { tab: 'intent', label: 'Intent' };
    case 'evidence':
      return { tab: 'evidence', label: 'Site evidence' };
    case 'reports':
      return { tab: 'reports', label: analysesLabel };
    case 'decision': {
      const tab = siteDecisionTab();
      return { tab, label: tab === 'decision' ? 'Decision' : analysesLabel };
    }
  }
}

function fmtMW(mw: number): string {
  return Math.round(mw).toLocaleString('en-US');
}

function reportTypeLabel(type: ReportTypeId): string {
  return REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type;
}

/** A display explanation of the current screening checklist, never a new gate. */
function currentScreeningProgress(site: OrbitSite): UnassessedNext {
  const progress = unassessedNext(site);
  // Location, study-area and evidence collection failures remain relevant at
  // every stage. Keep the shared navigation and explanation for those issues.
  if (progress.target === 'map' || site.site.sedStatus === 'bootstrapping' || site.site.sedStatus === 'failed') return progress;
  if (Date.parse(site.composite.lastComputedAt) === 0) return {
    why: 'Evidence summary needs refresh',
    next: 'Site inputs changed after this overview was computed. Review the current screening assessment before relying on earlier results.',
    cta: 'Review current analyses', target: 'reports',
  };
  const screening = site.composite.decisionStages?.screening;
  if (!screening) return {
    why: 'Screening requirements unavailable',
    next: 'Refresh the evidence summary to see the current screening checklist. No completed screening is inferred from a final-decision coverage score.',
    cta: 'Review current analyses', target: 'reports',
  };
  const savedMissing = screening.missingTypes.filter(type => site.savedAnalysisTypes?.includes(type));
  if (savedMissing.length > 0) return {
    why: 'Saved screening analyses need review',
    next: `${savedMissing.length} screening analysis type${savedMissing.length === 1 ? ' is' : 's are'} saved but not current: ${savedMissing.map(reportTypeLabel).join(', ')}. Open the site's Analyses to review quality holds, changed evidence and refresh requirements.`,
    cta: 'Review saved analyses', target: 'reports',
  };
  if (site.composite.decisionHold?.code === 'stage-1-incomplete' && site.verdict.dataAdequate) return {
    why: site.composite.decisionHold.label,
    next: site.composite.decisionHold.reason,
    cta: 'Review screening hold', target: 'decision',
  };
  if (screening.missingTypes.length > 0) return {
    why: screening.presentTypes.length > 0 ? 'Screening available with gaps' : 'No current screening analysis',
    next: screening.presentTypes.length > 0
      ? `Screening findings are available. Complete the current screening checklist: ${screening.missingTypes.map(reportTypeLabel).join(', ')}. Quality and evidence checks still apply.`
      : `Start the current screening assessment: ${screening.missingTypes.map(reportTypeLabel).join(', ')}. Project design choices can remain unknown during screening.`,
    cta: screening.presentTypes.length > 0 ? 'Review screening findings' : 'Start screening', target: 'reports',
  };
  if (site.stage.kind === 'screening') return {
    why: 'Review screening recommendations',
    next: 'Review the screened capacity, power and cooling options before choosing a project architecture for engineering validation.',
    cta: site.composite.decisionHold?.code === 'power-strategy-undeclared' ? 'Review Stage 2 decisions' : 'Review screening findings',
    target: site.composite.decisionHold?.code === 'power-strategy-undeclared' ? 'intent' : 'reports',
  };
  return progress;
}

/** Stage progress for the tile: Stage 1 fills the first half, Stage 2 the second. */
function stageProgressPct(stage: OrbitStage): number {
  const frac = stage.of > 0 ? stage.done / stage.of : 0;
  switch (stage.kind) {
    case 'unassessed': return 0;
    case 'screening': return frac * 50;
    case 'validating': return 50 + frac * 50;
    case 'decided': return 100;
  }
}

function stageProgressText(stage: OrbitStage): string {
  switch (stage.kind) {
    case 'unassessed': return '—';
    case 'screening': return stage.of > 0 ? `Stage 1 · ${stage.done}/${stage.of}` : 'Stage 1';
    case 'validating': return stage.of > 0 ? `Stage 2 · ${stage.done}/${stage.of}` : 'Stage 2';
    case 'decided': return 'Decided';
  }
}

function stageProgressNote(stage: OrbitStage): string {
  switch (stage.kind) {
    case 'unassessed': return 'current screening requirements pending';
    case 'screening': return 'early screening in progress';
    case 'validating': return stage.needsScenario ? 'awaiting a promoted scenario' : 'mature validation in progress';
    case 'decided': return 'published decision';
  }
}

/** One stage of the pipeline: its required analyses, ticked when present. */
function StageCard({
  status,
  stages,
  stage,
  testId,
  savedTypes = [],
  reportContexts = [],
}: {
  status: DecisionStageStatus;
  stages: DecisionStagesStatus;
  stage: OrbitStage;
  testId: string;
  savedTypes?: readonly ReportTypeId[];
  reportContexts?: readonly SavedScreeningReportContext[];
}) {
  const complete = status.missingTypes.length === 0 && !status.locked;
  const state = complete ? 'complete' : 'open';
  const nothingRequired = status.stage === 2 && stages.gridOnly && status.requiredTypes.length === 0;
  return (
    <div className="pm-rail-stage" data-state={state} data-testid={testId}>
      <div className="pm-rail-stage-h">
        <b>{DECISION_STAGE_LABEL[status.stage]}</b>
        <span>
          {nothingRequired
            ? 'nothing required'
            : `${status.presentTypes.length} of ${status.requiredTypes.length} used`}
        </span>
      </div>
      {status.requiredTypes.length > 0 ? (
        <ul>
          {status.requiredTypes.map((type) => {
            const present = status.presentTypes.includes(type);
            return (
              <li key={type} data-present={present}>
                {reportTypeLabel(type)}
                <small>{present ? 'Used in calculations' : savedTypes.includes(type) ? 'Saved · review needed' : 'No saved analysis'}</small>
              </li>
            );
          })}
        </ul>
      ) : null}
      {status.stage === 1 && status.outcome ? (
        <p
          className={`pm-rail-stage-note${status.outcome === 'clear' ? ' pm-rail-stage-note--muted' : ''}${status.outcome === 'declined' ? ' pm-rail-stage-note--danger' : ''}`}
          data-testid="orbit-rail-screening"
          data-outcome={status.outcome}
        >
          Saved screening result: {status.outcome === 'concerns' ? 'with concerns' : status.outcome}.
        </p>
      ) : null}
      {status.stage === 1 && status.outcome ? (
        <>
          <p className="pm-rail-stage-note pm-rail-stage-note--muted" data-testid="orbit-rail-screening-qualification">
            This classification can reflect scores, recorded decisions or planning constraints. It does not record a human approval or establish a verified permanent site disqualifier.
          </p>
          <details className="pm-rail-stage-note pm-rail-stage-note--muted" data-testid="orbit-rail-screening-context">
            <summary>Saved report context</summary>
            {reportContexts.length ? reportContexts.map(context => (
              <div key={context.reportId} className="mt-2 min-w-0 break-words">
                <p>{context.label}</p>
                <p>{context.date ? `Report dated ${context.date}.` : 'Report date unavailable.'}</p>
                <p>{context.revisionId ? `Saved immutable revision: ${context.revisionId}` : 'Immutable revision unavailable.'}</p>
                <a className="underline underline-offset-2" href={context.href}>Open current report</a>
                <p>{context.linkNotice}</p>
                <p>{context.currency}</p>
                {context.notes.map((note, index) => <p key={index} className="mt-1">{note}</p>)}
              </div>
            )) : <p>Source details are unavailable here. Review the saved screening report before relying on this result.</p>}
            <p className="mt-2">Referenced reports provide context; this list may not include every input to the screening classification. Verify the applicable source and its effective date for a specific disqualifying finding.</p>
          </details>
        </>
      ) : null}
      {status.stage === 1 && status.score != null ? (
        <p className="pm-rail-stage-note pm-rail-stage-note--muted" data-testid="orbit-rail-screening-score">
          {screeningScoreLabel(status.score, status.scoreCoverage, 'saved')}
        </p>
      ) : null}
      {status.locked ? (
        <p className="pm-rail-stage-note">
          Runs against a promoted generation scenario — solve and promote one in Generation &amp; Impact
          {stage.kind === 'screening' ? ' once early screening is complete' : ' first'}.
        </p>
      ) : null}
      {nothingRequired ? (
        <p className="pm-rail-stage-note pm-rail-stage-note--muted">
          Grid-only by declaration — no scenario to promote and no Stage 2 analysis required.
        </p>
      ) : null}
    </div>
  );
}

/** The pillar's benefit score is 0–5; render to one decimal, em-dash if missing. */
function pillarTone(score: number): string {
  if (score > 3.3) return 'is-go';
  if (score > 2) return 'is-cond';
  if (score > 0) return 'is-condhi';
  return 'is-low';
}

export function OrbitDetailRail({
  site,
  onResolve,
  onOpenSite,
  onClose,
}: {
  site: OrbitSite | null;
  /**
   * Resolve a withheld / constrained site: open the site tab that owns its
   * next step (see resolveOrbitTarget) — the tab the button names.
   */
  onResolve: (siteId: string, tab: string) => void;
  /** Open the site's full overview. */
  onOpenSite: (siteId: string) => void;
  /** Collapse the rail so the orbit reclaims the full panel width. */
  onClose?: () => void;
}) {
  if (!site) {
    return (
      <aside className="pm-rail" data-testid="orbit-detail-rail">
        {onClose ? (
          <button type="button" className="pm-rail-close" onClick={onClose} aria-label="Collapse detail">×</button>
        ) : null}
        <p className="pm-rail-empty">Select a site on the board to see its verdict, the evidence behind it, and the path to GO.</p>
      </aside>
    );
  }

  const { verdict, composite, mix, capacityMW, stage } = site;
  // Three states, in the order deriveSiteVerdict resolves them: the evidence
  // gate withholds first (INSUFFICIENT DATA), then a decision hold withholds
  // the band (DECISION PENDING — the two-stage pipeline or an undeclared
  // strategy), else the band is PUBLISHED.
  const published = verdict.band != null;
  // The scenario handoff is a portfolio station, not a completed validation
  // stage. Follow the same stage shown in this detail panel's heading.
  const screeningOnly = stage.kind === 'unassessed' || stage.kind === 'screening';
  const screeningProgress = published ? null : screeningOnly ? currentScreeningProgress(site) : unassessedNext(site);
  const held = !published && verdict.dataAdequate && composite.decisionHold != null;
  const scored = published;
  const accent = bandGlowColorVar(verdict.bandColor) ?? 'var(--text-muted)';
  const name = site.site.name ?? 'Unnamed site';
  const stages = composite.decisionStages;

  // WHY: the hold reason for a held site, the adequacy reason for a withheld
  // one, else the deterministic verdict rationale that produced the band.
  // Missing pillars come straight off the composite (where `.missing` is true).
  const why = published
    ? (composite.decisionRationale || 'All required diligence assessed — no withholding constraint.')
    : held
      ? composite.decisionHold!.reason
      : (verdict.adequacyReason ?? 'Verdict withheld — evidence not yet adequate.');
  const whyTitle = published ? 'Why this verdict' : held ? 'Why the decision is pending' : 'Why insufficient data';
  // Same frame as the coverage tile beside it (`decisionPillarCoverage` counts
  // the static non-advisory set). Filtering this list by applicability instead
  // named five gaps next to a tile reading "Current 2/9", and the two could not
  // be reconciled from the list alone — the other two showed up further down as
  // non-actionable "later" chips. The named gaps must sum to
  // applicablePillarCount - measuredPillarCount.
  const missingPillars = PILLAR_IDS.filter(
    (id) => {
      const pillar = composite.pillars?.[id];
      return !isAdvisoryPillar(id) && pillar?.missing;
    },
  );
  const unrunPillars = missingPillars.filter(id => !composite.pillars?.[id]?.dedicatedSourceUnreadable);
  const failedPillars = missingPillars.filter(id => composite.pillars?.[id]?.dedicatedSourceUnreadable);

  const total = mix?.total ?? 0;
  const mixParts = mix ? MIX_ORDER.filter((c) => mix[c] > 0) : [];

  const readinessPct = scored ? Math.round(site.readiness * 100) : null;
  const coverage = decisionPillarCoverage(composite);
  const coveragePct = Math.round(coverage.coverageRatio * 100);
  const trust = composite.confidenceTag;

  return (
    <aside className="pm-rail" data-testid="orbit-detail-rail" data-site={site.site.id}>
      {onClose ? (
        <button type="button" className="pm-rail-close" onClick={onClose} aria-label="Collapse detail" data-testid="orbit-rail-close">×</button>
      ) : null}
      {/* header — name + MW + the published decision as plain text. No per-site
          badge (owner, 2026-09-30): a withheld site shows no "Decision pending" /
          "Insufficient data" tag; the WHY block below is titled with that state. */}
      <div className="pm-rail-head">
        <div className="pm-rail-id">
          <button
            type="button"
            className="pm-rail-name"
            onClick={() => onOpenSite(site.site.id)}
            data-testid="orbit-rail-open"
          >
            {name}
          </button>
          <div className="pm-rail-mw">
            {capacityMW != null ? `${fmtMW(capacityMW)} MW · campus capacity` : 'Campus capacity TBD'}
          </div>
        </div>
        {published ? (
          <span
            className="pm-rail-verdict"
            style={{ '--c': accent } as React.CSSProperties}
            data-testid="orbit-rail-verdict"
          >
            {verdict.bandLabel}
          </span>
        ) : null}
      </div>

      {/* Explicit open action. The site name above is also a button, but it is styled
          as text and users did not find it — the rail read as a dead end. */}
      <button
        type="button"
        className="pm-rail-open"
        onClick={() => onOpenSite(site.site.id)}
        data-testid="orbit-rail-open-site"
        aria-label={`Open ${name}`}
      >
        Open site <span aria-hidden="true">→</span>
      </button>

      {/* the two-stage pipeline — where the site is and what lifts its hold */}
      {stages ? (
        <div className="pm-rail-stages" data-testid="orbit-rail-stages" data-stage={stage.kind}>
          <StageCard status={stages.screening} stages={stages} stage={stage} savedTypes={site.savedAnalysisTypes} reportContexts={site.screeningReportContexts} testId="orbit-rail-stage-1" />
          {!screeningOnly && <StageCard status={stages.validation} stages={stages} stage={stage} savedTypes={site.savedAnalysisTypes} testId="orbit-rail-stage-2" />}
          <p className="pm-rail-stage-note pm-rail-stage-note--muted">Current counts require the latest evidence and quality checks. Saved analyses remain available for review.</p>
        </div>
      ) : null}

      {screeningProgress && !screeningOnly && (
        <div className="pm-rail-why" data-testid="orbit-rail-screening-progress">
          <div className="pm-rail-why-t">{screeningProgress.why}</div>
          <p className="pm-rail-why-b">{screeningProgress.next}</p>
        </div>
      )}

      {/* WHY */}
      <div className={`pm-rail-why${scored ? '' : ' is-withheld'}`} data-testid={screeningOnly ? 'orbit-rail-screening-progress' : undefined}>
        <div className="pm-rail-why-t">{screeningOnly ? screeningProgress?.why : whyTitle}</div>
        <div className="pm-rail-why-b" data-testid="orbit-rail-why">{screeningOnly ? screeningProgress?.next : why}</div>
        {!screeningOnly && (unrunPillars.length > 0 || failedPillars.length > 0) ? (
          <ul className="pm-rail-wlist" data-testid="orbit-rail-missing">
            {unrunPillars.map((id) => (
              <li key={id}>{PILLARS[id].label} (Not yet run)</li>
            ))}
            {failedPillars.map((id) => (
              <li key={id}>{PILLARS[id].label} (Failed / unreadable)</li>
            ))}
          </ul>
        ) : null}
        {!scored ? (() => {
          const next = screeningOnly && stage.kind === 'unassessed' && screeningProgress
            ? { label: screeningProgress.cta, target: screeningProgress.target }
            : nextStepFor(site);
          const destination = resolveOrbitTarget(next.target);
          return (
            <button
              type="button"
              className="pm-rail-resolve"
              onClick={() => onResolve(site.site.id, destination.tab)}
              data-testid="orbit-rail-resolve"
            >
              {next.label} → {destination.label}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          );
        })() : null}
      </div>

      {screeningOnly ? (
        <p className="pm-rail-stage-note pm-rail-stage-note--muted" data-testid="orbit-rail-screening-scope">
          This view covers current screening requirements. A completed screening is not a final proceed decision.
        </p>
      ) : <>
      {/* readiness + coverage — two DISTINCT channels, for the decision stage */}
      <div className="pm-rail-dual">
        <div className="pm-rail-metric">
          <div className="pm-rail-mlab">{scored ? 'Readiness → GO' : 'Stage progress'}</div>
          <div className="pm-rail-mval" style={scored ? ({ '--c': accent } as React.CSSProperties) : undefined} data-testid="orbit-rail-progress">
            {scored ? `${readinessPct}%` : stageProgressText(stage)}
          </div>
          <div className="pm-rail-mbar">
            <i style={{ width: `${scored ? site.readiness * 100 : stageProgressPct(stage)}%`, ...(scored ? ({ '--c': accent } as React.CSSProperties) : {}) }} />
          </div>
          <div className="pm-rail-mnote">{scored ? 'distance to GO ceiling' : stageProgressNote(stage)}</div>
        </div>
        <div className="pm-rail-metric">
          <div className="pm-rail-mlab">Data Coverage</div>
          <div className="pm-rail-mval pm-rail-mval--cov">{coveragePct}%</div>
          <div className="pm-rail-mbar">
            <i className="pm-rail-mbar--cov" style={{ width: `${coverage.coverageRatio * 100}%` }} />
          </div>
          <div className="pm-rail-mnote" data-testid="orbit-rail-coverage-count">
            Current {coverage.measuredPillarCount}/{coverage.applicablePillarCount} decision pillars
          </div>
        </div>
      </div>

      {/* power mix */}
      <div className="pm-rail-block">
        <div className="pm-rail-bh">
          <span>Power Mix</span>
          <span className="pm-rail-bh-v">{total > 0 ? `${fmtMW(total)} MW` : 'no mix decided'}</span>
        </div>
        {mix && total > 0 ? (
          <>
            <div className="pm-rail-mixbar" data-testid="orbit-rail-mixbar">
              {mixParts.map((c) => (
                <span key={c} className={`pm-rail-mixseg pm-rail-mixseg--${c}`} style={{ flex: mix[c] }}>
                  {MIX_LABEL[c]} {fmtMW(mix[c])}
                </span>
              ))}
            </div>
            <div className="pm-rail-mixleg">
              {mixParts.map((c) => (
                <span key={c} className="pm-rail-mixleg-i">
                  <span className={`pm-rail-mixsw pm-rail-mixsw--${c}`} />
                  {MIX_LABEL[c]} {fmtMW(mix[c])} MW ({Math.round((mix[c] / total) * 100)}%)
                </span>
              ))}
            </div>
          </>
        ) : (
          <div className="pm-rail-gap">Power mix not yet decided for this site.</div>
        )}
      </div>

      {/* decision pillars */}
      <div className="pm-rail-block">
        <div className="pm-rail-bh">
          <span>Decision Pillars</span>
          <span className="pm-rail-bh-v">benefit · 0–5</span>
        </div>
        <div className="pm-rail-pillars" data-testid="orbit-rail-pillars">
          {PILLAR_IDS.map((id) => {
            const p = composite.pillars?.[id];
            const score = p?.score ?? 0;
            const isMissing = !p || p.missing;
            const applicable = isDecisionApplicablePillar(id, p);
            const inactiveLabel = isAdvisoryPillar(id) ? 'optional' : 'later';
            return (
              <div
                key={id}
                className={`pm-rail-pill${isMissing || !applicable ? ' is-miss' : ''}`}
                title={!applicable ? p?.applicabilityReason : undefined}
                data-applicability={applicable ? 'applicable' : 'not-applicable'}
              >
                <span className="pm-rail-pn">{PILLARS[id].label}</span>
                <span className="pm-rail-pt">
                  <i className={pillarTone(score)} style={{ width: `${isMissing || !applicable ? 0 : (score / 5) * 100}%` }} />
                </span>
                <span className="pm-rail-pv">
                  {!applicable ? inactiveLabel : isMissing ? (p?.dedicatedSourceUnreadable ? 'unreadable' : 'awaiting') : score.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* data trust */}
      <div className="pm-rail-block">
        <div className="pm-rail-bh"><span>Data Trust</span></div>
        <div className="pm-rail-trust" data-testid="orbit-rail-trust">
          <div className={`pm-rail-tg pm-rail-tg--${trust.toLowerCase()}`}>{trust}</div>
          <div className="pm-rail-tx">{TRUST_MEANING[trust]}</div>
        </div>
      </div>
      </>}
    </aside>
  );
}

export default OrbitDetailRail;
